import React from "react";
import { SecondaryButton } from "./Button";

/* ── App Brand Palette ── */
// --primaryColor:  #3f88a5  (steel blue)
// --secondaryColor: #1f9ed1 (bright blue)
// --tertiaryColor: #011627  (dark navy)
// --neutralColor:  #ffffff

/* ── Icon SVGs ── */
const SuccessIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="#1f9ed1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ErrorIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="#e05a5a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const WarningIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="#3f88a5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);

const VARIANTS = {
    success: {
        icon: <SuccessIcon />,
        iconBg: "#e8f4fa",           // light blue tint
        iconBorder: "#1f9ed1",       // secondaryColor
        confirmBg: "#1f9ed1",
        confirmTextColor: "#ffffff",
        accentBar: "#1f9ed1",
    },
    error: {
        icon: <ErrorIcon />,
        iconBg: "#fdeaea",
        iconBorder: "#e05a5a",
        confirmBg: "#e05a5a",
        confirmTextColor: "#ffffff",
        accentBar: "#e05a5a",
    },
    warning: {
        icon: <WarningIcon />,
        iconBg: "#e8f4fa",           // still blue-tinted, matching app theme
        iconBorder: "#3f88a5",       // primaryColor
        confirmBg: "#3f88a5",
        confirmTextColor: "#ffffff",
        accentBar: "#3f88a5",
    },
};

/**
 * Alert — custom SweetAlert-style modal.
 *
 * Props:
 *   show        {boolean}
 *   type        'success' | 'error' | 'warning'   default 'success'
 *   title       {string}
 *   message     {string}
 *   confirmText {string}   default 'OK'
 *   cancelText  {string}   optional — renders a cancel button when provided
 *   onConfirm   {function}
 *   onCancel    {function} optional
 */
const Alert = ({
    show,
    type = "success",
    title,
    message,
    confirmText = "OK",
    cancelText,
    onConfirm,
    onCancel,
}) => {
    if (!show) return null;

    const v = VARIANTS[type] ?? VARIANTS.success;

    return (
        <div
            style={{
                position: "fixed", inset: 0, zIndex: 11000,
                background: "rgba(1, 22, 39, 0.55)",   // tertiaryColor-tinted overlay
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "20px",
                backdropFilter: "blur(2px)",
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
        >
            <div style={{
                width: "100%", maxWidth: 400,
                background: "var(--surface-2, #f9fafb)",
                borderRadius: "14px",
                border: `1.5px solid ${v.iconBorder}`,
                boxShadow: "0 8px 32px rgba(31, 158, 209, 0.18), 0 2px 8px rgba(1,22,39,0.12)",
                display: "flex", flexDirection: "column",
                alignItems: "center",
                overflow: "hidden",
                textAlign: "center",
            }}>
                {/* Top accent bar */}
                <div style={{
                    width: "100%", height: 5,
                    background: `linear-gradient(90deg, #3f88a5 0%, #1f9ed1 100%)`,
                    flexShrink: 0,
                }} />

                <div style={{
                    display: "flex", flexDirection: "column",
                    alignItems: "center",
                    padding: "28px 24px 24px",
                    gap: "16px",
                    width: "100%",
                    boxSizing: "border-box",
                }}>
                    {/* Icon */}
                    <div style={{
                        width: 64, height: 64,
                        borderRadius: "50%",
                        background: v.iconBg,
                        border: `2px solid ${v.iconBorder}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                        boxShadow: `0 0 0 6px ${v.iconBg}`,
                    }}>
                        {v.icon}
                    </div>

                    {/* Text */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {title && (
                            <div style={{
                                fontSize: "18px", fontWeight: 700,
                                color: "#011627",
                                fontFamily: "inherit",
                                lineHeight: 1.3,
                            }}>
                                {title}
                            </div>
                        )}
                        {message && (
                            <div style={{
                                fontSize: "14px", color: "#4a6572",
                                fontFamily: "inherit", lineHeight: 1.6,
                            }}>
                                {message}
                            </div>
                        )}
                    </div>

                    {/* Buttons */}
                    {(confirmText || cancelText) && (
                        <div style={{ display: "flex", gap: "8px", justifyContent: "center", width: "100%" }}>
                            {cancelText && (
                                <SecondaryButton
                                    style={{ flex: 1, height: 38, fontSize: "14px" }}
                                    onClick={onCancel}
                                >
                                    {cancelText}
                                </SecondaryButton>
                            )}
                            {confirmText && (
                                <button
                                    onClick={onConfirm}
                                    style={{
                                        flex: 1, height: 38,
                                        fontSize: "14px", fontWeight: 700,
                                        fontFamily: "inherit",
                                        background: `linear-gradient(135deg, #3f88a5 0%, #1f9ed1 100%)`,
                                        color: "#ffffff",
                                        border: "none",
                                        borderRadius: "7px",
                                        cursor: "pointer",
                                        transition: "opacity 0.15s ease",
                                        letterSpacing: "0.3px",
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.88"; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                                >
                                    {confirmText}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Alert;
